import { useEffect, useRef, useState } from 'react';
import { validateSignupContact } from '../utils/posSignupUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { POS_SIGNUP_FIELD_MAX } from '../constants/posConst';
import { LANDING_PRIVACY_URL } from '../constants/posLandingConst';

/** The contact fields, in order. */
const FIELDS = [
  { key: 'name', label: 'Full Name', placeholder: 'Juan dela Cruz', autoComplete: 'name', type: 'text' },
  { key: 'business', label: 'Business Name', placeholder: "Juan's Store & Cafe", autoComplete: 'organization', type: 'text' },
  { key: 'email', label: 'Email Address', placeholder: 'juandelacruz@email.com', autoComplete: 'email', type: 'email' },
  { key: 'phone', label: 'Contact Number', placeholder: '0917...', autoComplete: 'tel', type: 'tel' },
];

/**
 * Step one of a paid checkout — and the whole of a free-trial sign-up: who
 * is subscribing.
 *
 * Empty fields are outlined and the first is focused, as in the design; a
 * malformed email address is caught here too, before the visitor pays.
 *
 * @param {object} props
 * @param {{name: string, business: string, email: string, phone: string}} props.value
 * @param {(value: object) => void} props.onChange
 * @param {() => void} props.onContinue - called with valid details
 * @param {string} [props.submitLabel] - the button's wording
 * @param {boolean} [props.busy] - a save is in flight; the button is disabled
 * @param {string} [props.saveError] - why the last save failed, from the parent
 * @returns {JSX.Element}
 */
function PosLandingDetailsForm({
  value, onChange, onContinue, submitLabel = 'Continue', busy = false, saveError = '',
}) {
  const [bad, setBad] = useState({});
  const [error, setError] = useState('');
  const inputRefs = useRef({});

  useEffect(() => {
    const timer = setTimeout(() => inputRefs.current.name?.focus(), 60);
    return () => clearTimeout(timer);
  }, []);

  /**
   * @param {React.FormEvent} event
   * @returns {void}
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    const missing = FIELDS.filter((field) => !String(value[field.key] || '').trim()).map((field) => field.key);
    setBad(Object.fromEntries(missing.map((key) => [key, true])));
    if (missing.length) {
      setError('');
      inputRefs.current[missing[0]]?.focus();
      return;
    }
    try {
      validateSignupContact(value);
    } catch (validationError) {
      setError(validationError.message);
      if (/email/i.test(validationError.message)) {
        setBad({ email: true });
        inputRefs.current.email?.focus();
      }
      return;
    }
    setError('');
    onContinue();
  };

  return (
    <form id="subForm" noValidate onSubmit={handleSubmit}>
      <p className="privacy-note">
        Your details are stored securely and used only for billing, account setup
        and our internal client records. We never sell or share them.
      </p>
      {FIELDS.map((field) => (
        <label key={field.key}>
          {field.label} <i>*</i>
          <input
            ref={(element) => { inputRefs.current[field.key] = element; }}
            name={field.key}
            type={field.type}
            placeholder={field.placeholder}
            autoComplete={field.autoComplete}
            maxLength={POS_SIGNUP_FIELD_MAX[field.key]}
            className={bad[field.key] ? 'bad' : undefined}
            value={value[field.key] || ''}
            onChange={(event) => {
              onChange({ ...value, [field.key]: event.target.value });
              if (bad[field.key]) setBad((current) => ({ ...current, [field.key]: false }));
            }}
            required
          />
        </label>
      ))}
      <p className="consent">
        By submitting this form, you consent to the collection and use of your
        information as described in our
        {' '}
        {LANDING_PRIVACY_URL
          ? <a href={LANDING_PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
          : 'Privacy Policy'}
        .
      </p>
      {(error || saveError) && <p className="pick-warn">{error || saveError}</p>}
      {busy && <div className="plan-state">{spinnerRing('Submitting')}</div>}
      <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>{submitLabel}</button>
    </form>
  );
}

export default PosLandingDetailsForm;
